import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  Link,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  PhotoCamera as PhotoCameraIcon,
  EmojiEvents as TrophyIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ZoomResetIcon,
  RotateRight as RotateRightIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useUser } from './UserContext';
import api from './api';
import heic2any from 'heic2any';
import { useNavigate } from 'react-router-dom';

const ContestPage = () => {
  const { user } = useUser();
  const { clubShortName, contestShortName } = useParams();
  const fileInputRef = useRef(null);
  const recaptchaRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const photoTitleRef = useRef(null);
  const photoLocationRef = useRef(null);
  const photoPhotographerRef = useRef(null);
  const navigate = useNavigate();

  // State
  const [contest, setContest] = useState(null);
  const [myPhotos, setMyPhotos] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [votes, setVotes] = useState([]);
  const [allVotes, setAllVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [club, setClub] = useState(null);

  // Photo upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoLocation, setPhotoLocation] = useState('');
  const [photoPhotographer, setPhotoPhotographer] = useState('');
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [photoTitleError, setPhotoTitleError] = useState(false);
  const [photoLocationError, setPhotoLocationError] = useState(false);
  const [photoPhotographerError, setPhotoPhotographerError] = useState(false);

  // Bulk upload state (galleries, owner/committee only)
  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Photo viewer modal state
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);

  const [sortByVote, setSortByVote] = useState(false);
  const [savedGuids, setSavedGuids] = useState(new Set());

  // ================= Photo editing
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editPhotoTitle, setEditPhotoTitle] = useState('');
  const [editPhotoLocation, setEditPhotoLocation] = useState('');
  const [editPhotoPhotographer, setEditPhotoPhotographer] = useState('');
  const [editError, setEditError] = useState('');
  const [editing, setEditing] = useState(false);

  const handleEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setEditPhotoTitle(photo.photoTitle ?? '');
    setEditPhotoLocation(photo.photoLocation ?? '');
    setEditPhotoPhotographer(photo.photoPhotographer ?? '');
    setEditError('');
    setShowEditModal(true);
  };

  const resetEditModal = () => {
    setShowEditModal(false);
    setEditingPhoto(null);
    setEditPhotoTitle('');
    setEditPhotoLocation('');
    setEditPhotoPhotographer('');
    setEditError('');
  };

  const handlePhotoEdit = async () => {
    if (!contest?.contestIsGallery && (!editPhotoTitle || !editPhotoLocation || !editPhotoPhotographer)) {
      setEditError('Please fill in all required fields');
      return;
    }

    setEditing(true);
    setEditError('');

    const formData = new FormData();
    formData.append('photoTitle', editPhotoTitle);
    formData.append('photoLocation', editPhotoLocation);
    formData.append('photoPhotographer', editPhotoPhotographer);

    try {
      await api.post(`/photo/${editingPhoto.photoGuid}/edit`, formData, {
        headers: {
          'Authentication': user.authentication,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update the photo in both myPhotos and allPhotos arrays
      const updatePhoto = (photo) => {
        if (photo.photoGuid === editingPhoto.photoGuid) {
          return {
            ...photo,
            photoTitle: editPhotoTitle,
            photoLocation: editPhotoLocation,
            photoPhotographer: editPhotoPhotographer
          };
        }
        return photo;
      };

      setMyPhotos(myPhotos.map(updatePhoto));
      setAllPhotos(allPhotos.map(updatePhoto));

      resetEditModal();
    } catch (err) {
      setEditError(err.response?.data?.message || err.response?.data || 'Failed to update photo');
    } finally {
      setEditing(false);
    }
  };

  // Load contest data
  useEffect(() => {
    const fetchContestData = async () => {
      try {
        setLoading(true);

        // Fetch contest details
        const contestResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}`, {
          headers: { 'Authentication': user.authentication }
        });
        setContest(contestResponse.data.result);

        // Fetch club
        const clubResponse = await api.get(`/club/${clubShortName}`, {
          headers: { 'Authentication': user.authentication }
        });
        setClub(clubResponse.data.result);

        // Fetch photos
        const photosResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}/photos`, {
          headers: { 'Authentication': user.authentication }
        });

        // Handle new response structure
        const photosData = photosResponse.data.result || {};
        setMyPhotos(photosData.myPhotos || []);
        setAllPhotos(photosData.allPhotos || []);

        // Fetch votes only for contests (not galleries)
        const contestData = contestResponse.data.result;
        if (!contestData.contestIsGallery) {
          const now = new Date();
          const votesStart = new Date(contestData.contestVotesStart * 1000);

          if (now >= votesStart) {
            const votesResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}/votes`, {
              headers: { 'Authentication': user.authentication }
            });
            setVotes(votesResponse.data.result.myVotes || []);
            setAllVotes(votesResponse.data.result.allVotes || []);
          }
        }
      } catch (err) {
        setError('Failed to load contest data');
        console.error('Error fetching contest:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.authentication && clubShortName && contestShortName) {
      fetchContestData();
    }
  }, [user, clubShortName, contestShortName]);

  // Measure image scale when photo viewer opens
  const measureImageScale = () => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;

    // Ensure image is loaded
    if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
      console.warn('Image not fully loaded for scale measurement');
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    if (naturalWidth && naturalHeight && containerWidth && containerHeight) {
      // Calculate the scale needed to fit the image within the container
      const scaleX = containerWidth / naturalWidth;
      const scaleY = containerHeight / naturalHeight;
      const actualScale = Math.min(scaleX, scaleY);

      console.log('Container size:', containerWidth, 'x', containerHeight);
      console.log('Natural size:', naturalWidth, 'x', naturalHeight);
      console.log('Calculated scale:', actualScale);

      // Update both baseScale and zoomLevel to reflect the fitted size
      setBaseScale(actualScale);
      setZoomLevel(actualScale);
    }
  };

  const getGalleryStatus = (gallery) => {
    if (!gallery.contestGalleryEndDate) return { status: 'Active', color: 'success' };
    const now = Math.floor(Date.now() / 1000);
    if (now >= gallery.contestGalleryEndDate) return { status: 'Closed', color: 'default' };
    return { status: 'Active', color: 'success' };
  };

  const getContestStatus = (contest) => {
    const now = new Date();
    const uploadsStart = new Date(contest.contestUploadsStart * 1000);
    const uploadsEnd = new Date(contest.contestUploadsEnd * 1000);
    const votesStart = new Date(contest.contestVotesStart * 1000);
    const votesEnd = new Date(contest.contestVotesEnd * 1000);

    if (now < uploadsStart) {
      return { status: 'Upcoming', color: 'default' };
    } else if (now >= uploadsStart && now <= uploadsEnd) {
      return { status: 'Uploads Open', color: 'primary' };
    } else if (now > uploadsEnd && now < votesStart) {
      return { status: 'Awaiting Voting', color: 'warning' };
    } else if (now >= votesStart && now <= votesEnd) {
      return { status: 'Voting Open', color: 'success' };
    } else if (contest.contestShowResults) {
      return { status: 'Contest Results', color: 'secondary' };
    } else {
      return { status: 'Awaiting Results', color: 'warning' };
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isOwnerOrCommittee = () => {
    if (!contest) return false;
    const ooc = contest.ownerUserEmail === user.userEmail ||
           (contest.contestCommittee && contest.contestCommittee.includes(user.userEmail));
    return ooc;
  };

  const getRemainingUploads = () => {
    if (!contest) return 0;
    if (contest.contestIsGallery) {
      if (!club) return 0;
      const galleryLimit = club.maxMembers * contest.contestMaxUploads;
      return Math.max(0, galleryLimit - allPhotos.length);
    }
    return Math.max(0, contest.contestMaxUploads - myPhotos.length);
  };

  const getMyVotes = () => {
    return votes.filter(vote => vote.userVoteGuid === user.userGuid);
  };

  const getRemainingVotes = () => {
    if (!contest) return 0;
    const myVotesCount = getMyVotes().length;
    return Math.max(0, contest.contestMaxVotes - myVotesCount);
  };

  const hasVotedForPhoto = (photoGuid) => {
    return votes.some(vote => vote.photoGuid === photoGuid && vote.userVoteGuid === user.userGuid);
  };

  const [fileSizeError, setFileSizeError] = useState('');
  const [convertingHeic, setConvertingHeic] = useState(false);

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview('');
    setPhotoTitle('');
    setPhotoLocation('');
    setPhotoPhotographer('');
    setAcceptPolicy(false);
    setRecaptchaValue('');
    setUploadError('');
    setFileSizeError('');
    setPhotoTitleError(false);
    setPhotoLocationError(false);
    setPhotoPhotographerError(false);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRotate = () => {
    if (!uploadFile) return;
    const img = new Image();
    const objectUrl = URL.createObjectURL(uploadFile);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob((blob) => {
        const rotatedFile = new File([blob], uploadFile.name, { type: 'image/jpeg' });
        setUploadFile(rotatedFile);
        setUploadPreview(URL.createObjectURL(rotatedFile));
      }, 'image/jpeg', 0.95);
    };
    img.src = objectUrl;
  };

  // ================= Bulk upload (galleries, owner/committee only)

  const initBulkQueue = async (files) => {
    // Add placeholder items immediately so the modal opens right away
    const placeholders = files.map(f => ({
      id: Math.random().toString(36).slice(2),
      name: f.name,
      processedFile: null,
      thumbFile: null,
      previewUrl: '',
      status: 'processing',
      error: null,
    }));
    setBulkQueue(placeholders);
    setShowBulkModal(true);

    // Process each file through the same pipeline as single uploads
    for (let i = 0; i < files.length; i++) {
      const id = placeholders[i].id;
      try {
        const processedFile = await processImageFile(files[i]);
        if (!processedFile) throw new Error('Failed to process image');
        const thumbFile = await generateThumbnail(processedFile, 320).catch(() => null);
        const previewUrl = URL.createObjectURL(thumbFile ?? processedFile);
        setBulkQueue(q => q.map(item =>
          item.id === id ? { ...item, processedFile, thumbFile, previewUrl, status: 'pending' } : item
        ));
      } catch {
        setBulkQueue(q => q.map(item =>
          item.id === id ? { ...item, status: 'rejected', error: 'Could not process file' } : item
        ));
      }
    }
  };

  const handleBulkRotate = (id) => {
    const item = bulkQueue.find(i => i.id === id);
    if (!item || !item.processedFile) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(item.processedFile);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob(async (blob) => {
        const rotatedFile = new File([blob], item.processedFile.name, { type: 'image/jpeg' });
        const thumbFile = await generateThumbnail(rotatedFile, 320).catch(() => null);
        const previewUrl = URL.createObjectURL(thumbFile ?? rotatedFile);
        setBulkQueue(q => q.map(i =>
          i.id === id ? { ...i, processedFile: rotatedFile, thumbFile, previewUrl } : i
        ));
      }, 'image/jpeg', 0.95);
    };
    img.src = objectUrl;
  };

  const handleBulkUpload = async () => {
    setBulkUploading(true);
    const pending = bulkQueue.filter(i => i.status === 'pending');

    for (const item of pending) {
      setBulkQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'uploading' } : i));
      try {
        const formData = new FormData();
        formData.append('photo', item.processedFile);
        if (item.thumbFile) formData.append('photo_thumb', item.thumbFile);
        // No captchaToken — server skips captcha for gallery owner/committee

        await api.post(`/club/${clubShortName}/contest/${contestShortName}/photo`, formData, {
          headers: {
            'Authentication': user.authentication,
            'Content-Type': 'multipart/form-data',
          }
        });
        setBulkQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'done' } : i));
      } catch (err) {
        const raw = err.response?.data;
        const msg = (typeof raw === 'string' ? raw : raw?.message) || 'Upload failed';
        const userMsg = (msg.includes('Content Violates Acceptable Use Policy') || msg.includes('CONTENT_VIOLATES_POLICY'))
          ? 'Content violates Acceptable Use Policy'
          : msg;
        setBulkQueue(q => q.map(i => i.id === item.id ? { ...i, status: 'rejected', error: userMsg } : i));
      }
    }
    setBulkUploading(false);
  };

  const closeBulkModal = async () => {
    if (bulkQueue.some(i => i.status === 'done')) {
      try {
        const photosResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}/photos`, {
          headers: { 'Authentication': user.authentication }
        });
        const photosData = photosResponse.data.result || {};
        setMyPhotos(photosData.myPhotos || []);
        setAllPhotos(photosData.allPhotos || []);
      } catch { /* ignore */ }
    }
    bulkQueue.forEach(item => { if (item.previewUrl) URL.revokeObjectURL(item.previewUrl); });
    setBulkQueue([]);
    setBulkUploading(false);
    setShowBulkModal(false);
  };

  const handlePhotoUpload = async (captchaValue = recaptchaValue) => {
    const needsMetadata = !contest?.contestIsGallery;
    if (!uploadFile || (needsMetadata && (!photoTitle || !photoLocation || !photoPhotographer)) || !acceptPolicy) {
      setUploadError('Please fill in all required fields');
      return;
    }

    if (!captchaValue) {
      setUploadError('Please complete the security verification');
      return;
    }

    setUploading(true);
    setUploadError('');

    // Generate a ≤5 MB version for Rekognition (CF Workers cannot resize server-side)
    const rekogFile = uploadFile.size > 5 * 1024 * 1024
      ? await compressImage(uploadFile, 5 * 1024 * 1024).catch(() => null)
      : null;

    // Generate 320px-wide thumbnail client-side (320px ensures both dimensions ≥ 160px
    // for common aspect ratios, which is required for PhotoDNA CSAM hashing server-side)
    const thumbFile = await generateThumbnail(uploadFile, 320).catch(() => null);

    const formData = new FormData();
    formData.append('photo', uploadFile);
    if (rekogFile) formData.append('photo_rekog', rekogFile);
    if (thumbFile) formData.append('photo_thumb', thumbFile);
    formData.append('photoTitle', photoTitle);
    formData.append('photoLocation', photoLocation);
    formData.append('photoPhotographer', photoPhotographer);
    formData.append('captchaToken', captchaValue);

    try {
      await api.post(`/club/${clubShortName}/contest/${contestShortName}/photo`, formData, {
        headers: {
          'Authentication': user.authentication,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Refresh photos
      const photosResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}/photos`, {
        headers: { 'Authentication': user.authentication }
      });

      const photosData = photosResponse.data.result || {};
      setMyPhotos(photosData.myPhotos || []);
      setAllPhotos(photosData.allPhotos || []);

      resetUploadModal();
    } catch (err) {
      // Defer reCAPTCHA reset to avoid crashing DOM ops mid-render
      setRecaptchaValue('');
      setTimeout(() => {
        if (recaptchaRef.current) {
          try {
            recaptchaRef.current.reset();
          } catch (error) {
            console.warn('ReCAPTCHA reset failed:', error);
          }
        }
      }, 0);
      if (err.response?.status === 400) {
        const raw = err.response?.data
        const errorMessage = (typeof raw === 'string' ? raw : raw?.message) || err.response?.statusText || '';

        if (errorMessage.includes('Content Violates Acceptable Use Policy') ||
            errorMessage.includes('CONTENT_VIOLATES_POLICY')) {
          setUploadError('This image violates our Acceptable Use Policy and cannot be uploaded. Please review our content guidelines and select a different photo.');
        } else {
          setUploadError(errorMessage || 'Upload failed. Please try again.');
        }
      } else {
        setUploadError(err.response?.data?.message || err.response?.data || 'Failed to upload photo');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    const needsMetadata = !contest?.contestIsGallery;
    if (needsMetadata) {
      if (!photoTitle) {
        setPhotoTitleError(true);
        photoTitleRef.current?.querySelector('input')?.focus();
        return;
      }
      if (!photoLocation) {
        setPhotoLocationError(true);
        photoLocationRef.current?.querySelector('input')?.focus();
        return;
      }
      if (!photoPhotographer) {
        setPhotoPhotographerError(true);
        photoPhotographerRef.current?.querySelector('input')?.focus();
        return;
      }
    }
    if (!uploadFile || !acceptPolicy) {
      setUploadError('Please fill in all required fields');
      return;
    }

    setUploadError('');

    try {
      if (recaptchaRef.current) {
        recaptchaRef.current.execute().catch(() => {});
      } else {
        setUploadError('Security verification not ready. Please try again.');
      }
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      setUploadError('Security verification failed. Please refresh the page and try again.');
    }
  };

  const handleDeletePhoto = async (photoGuid) => {
    try {
      await api.delete(`/photo/${photoGuid}`, {
        headers: { 'Authentication': user.authentication }
      });

      setMyPhotos(myPhotos.filter(photo => photo.photoGuid !== photoGuid));
      setAllPhotos(allPhotos.filter(photo => photo.photoGuid !== photoGuid));
    } catch (err) {
      console.error('Error deleting photo:', err);
    }
  };

  const handleVoteToggle = async (photoGuid) => {
    const hasVoted = hasVotedForPhoto(photoGuid);

    try {
      if (hasVoted) {
        await api.delete(`/club/${clubShortName}/contest/${contestShortName}/vote/${photoGuid}`, {
          headers: { 'Authentication': user.authentication }
        });

        setVotes(votes.filter(vote => !(vote.photoGuid === photoGuid && vote.userVoteGuid === user.userGuid)));
        setAllPhotos(allPhotos.map(p => p.photoGuid === photoGuid ? { ...p, voteCount: (p.voteCount ?? 0) - 1 } : p));
      } else {
        const formData = new FormData();
        formData.append('photoGuid', photoGuid);

        await api.post(`/club/${clubShortName}/contest/${contestShortName}/vote`, formData, {
          headers: {
            'Authentication': user.authentication,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        setVotes([...votes, { photoGuid, userVoteGuid: user.userGuid, userPhotoGuid: allPhotos.filter(photo => photo.photoGuid === photoGuid).userPhotoGuid }]);
        setAllPhotos(allPhotos.map(p => p.photoGuid === photoGuid ? { ...p, voteCount: (p.voteCount ?? 0) + 1 } : p));
      }

      setSavedGuids(prev => new Set(prev).add(photoGuid));
      setTimeout(() => {
        setSavedGuids(prev => { const next = new Set(prev); next.delete(photoGuid); return next; });
      }, 1000);
    } catch (err) {
      console.error('Error toggling vote:', err);
    }
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
    setShowPhotoViewer(true);
    setZoomLevel(1);
    setBaseScale(1);
    setPanPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const handleClosePhotoViewer = () => {
    setShowPhotoViewer(false);
    setSelectedPhoto(null);
    setZoomLevel(1);
    setBaseScale(1);
    setPanPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, baseScale * 0.1));
  };

  const handleZoomReset = () => {
    setZoomLevel(baseScale);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleImageDoubleClick = () => {
    if (Math.abs(zoomLevel - baseScale) < 0.01) {
      setZoomLevel(baseScale * 2);
    } else {
      handleZoomReset();
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > baseScale) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > baseScale) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const getTouchDistance = (touch1, touch2) => {
    return Math.sqrt(
      Math.pow(touch1.clientX - touch2.clientX, 2) +
      Math.pow(touch1.clientY - touch2.clientY, 2)
    );
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setLastTouchDistance(distance);
    } else if (e.touches.length === 1 && zoomLevel > baseScale) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panPosition.x,
        y: e.touches[0].clientY - panPosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && lastTouchDistance > 0) {
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = distance / lastTouchDistance;
      setZoomLevel(prev => Math.min(Math.max(prev * scale, baseScale * 0.1), 5));
      setLastTouchDistance(distance);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > baseScale) {
      setPanPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setLastTouchDistance(0);
  };

  const getPhotoRanking = (photoArray) => {
    return photoArray.sort((a, b) => b.voteCount - a.voteCount);
  };

  // HEIC conversion
  const convertHeicToJpeg = async (file) => {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      });

      // Create a new File object from the converted blob
      const convertedFile = new File([convertedBlob],
        file.name.replace(/\.heic$/i, '.jpg'),
        { type: 'image/jpeg' }
      );

      return convertedFile;
    } catch (error) {
      console.error('HEIC conversion failed:', error);
      throw new Error('Failed to convert HEIC image. Please try a different format.');
    }
  };

  const isHeicFile = (file) => {
    return file.type === 'image/heic' ||
          file.type === 'image/HEIC' ||
          file.name.toLowerCase().endsWith('.heic');
  };

  const compressImage = async (file, maxSizeInBytes = 20 * 1024 * 1024) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Set canvas dimensions to image dimensions
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // Draw the image on canvas
        ctx.drawImage(img, 0, 0);

        // Function to try different quality levels
        const tryCompress = (quality) => {
          return new Promise((resolveCompress) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                resolveCompress(null);
                return;
              }

              // Create a new File object from the compressed blob
              const compressedFile = new File([blob],
                file.name.replace(/\.(png|gif|bmp|tiff?|webp)$/i, '.jpg'),
                { type: 'image/jpeg' }
              );

              resolveCompress({
                file: compressedFile,
                size: blob.size,
                quality: quality
              });
            }, 'image/jpeg', quality);
          });
        };

        // Try quality levels in 0.05 steps from 1.0 down to 0.05
        const attemptCompression = async () => {
          const qualities = [1.0, 0.95, 0.90, 0.85, 0.80, 0.75, 0.70, 0.65, 0.60, 0.55, 0.50, 0.45, 0.40, 0.35, 0.30, 0.25, 0.20, 0.15, 0.10, 0.05];

          for (const quality of qualities) {
            const result = await tryCompress(quality);

            if (!result) {
              continue;
            }

            console.log(`Compression attempt: quality ${quality}, size: ${(result.size / (1024 * 1024)).toFixed(1)}MB`);

            if (result.size <= maxSizeInBytes) {
              console.log(`Successfully compressed to ${(result.size / (1024 * 1024)).toFixed(1)}MB at quality ${quality}`);
              resolve(result.file);
              return;
            }
          }

          // If we get here, even maximum compression didn't work
          reject(new Error(`Unable to compress image below ${(maxSizeInBytes / (1024 * 1024)).toFixed(0)}MB limit. Please try a smaller image.`));
        };

        attemptCompression();
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };

      // Load the image
      img.src = URL.createObjectURL(file);
    });
  };

  const generateThumbnail = (file, maxWidth = 200) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.naturalWidth);
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Thumbnail generation failed')); return; }
          resolve(new File([blob], 'thumb.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.9);
      };

      img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
      img.src = URL.createObjectURL(file);
    });
  };

  const stripExifFromFile = async (file) => {
    const buffer = await file.arrayBuffer()
    const view = new DataView(buffer)

    // Only process JPEG files (SOI marker FF D8)
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return file

    const segments = []
    let offset = 2 // skip SOI

    while (offset + 4 <= view.byteLength) {
      if (view.getUint8(offset) !== 0xff) break
      const marker = view.getUint16(offset)
      if (marker === 0xffda) { // SOS — rest is raw scan data, not segmented
        segments.push({ offset, length: view.byteLength - offset })
        break
      }
      const segLength = view.getUint16(offset + 2) + 2
      // Drop APP1 segments that contain "Exif\0\0"
      const isExifApp1 =
        marker === 0xffe1 &&
        segLength > 10 &&
        view.getUint32(offset + 4) === 0x45786966 && // "Exif"
        view.getUint16(offset + 8) === 0x0000
      if (!isExifApp1) segments.push({ offset, length: segLength })
      offset += segLength
    }

    // Reassemble: SOI + kept segments
    const soi = new Uint8Array(buffer, 0, 2)
    const parts = [soi, ...segments.map(s => new Uint8Array(buffer, s.offset, s.length))]
    const totalLength = parts.reduce((n, p) => n + p.byteLength, 0)
    const out = new Uint8Array(totalLength)
    let pos = 0
    for (const part of parts) { out.set(part, pos); pos += part.byteLength }
    return new File([out], file.name, { type: file.type })
  }

  const processImageFile = async (file) => {
    const maxSizeInBytes = 20 * 1024 * 1024; // 20MB
    let processedFile = file;

    try {
      // First, handle HEIC conversion if needed
      if (isHeicFile(file)) {
        setConvertingHeic(true);
        processedFile = await convertHeicToJpeg(file);
        setConvertingHeic(false);
      }

      // Check if compression is needed
      if (processedFile.size > maxSizeInBytes) {
        console.log(`File size (${(processedFile.size / (1024 * 1024)).toFixed(1)}MB) exceeds limit. Compressing...`);

        // Show compression status
        setFileSizeError(`Compressing image (${(processedFile.size / (1024 * 1024)).toFixed(1)}MB)...`);

        try {
          processedFile = await compressImage(processedFile, maxSizeInBytes);
          console.log(`Compression successful: ${(processedFile.size / (1024 * 1024)).toFixed(1)}MB`);
          setFileSizeError(''); // Clear the compression message
        } catch (compressionError) {
          console.error('Compression failed:', compressionError);
          setFileSizeError(compressionError.message);
          return null;
        }
      } else {
        // File is already under the limit
        setFileSizeError('');
      }

      processedFile = await stripExifFromFile(processedFile)
      return processedFile;

    } catch (error) {
      console.error('File processing error:', error);
      if (isHeicFile(file)) {
        setConvertingHeic(false);
      }
      setFileSizeError(error.message || 'Failed to process image file.');
      return null;
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';
    if (!files.length) return;

    if (isOwnerOrCommittee() && contest?.contestIsGallery && files.length > 1) {
      initBulkQueue(files);
      return;
    }

    const file = files[0];
    try {
      const processedFile = await processImageFile(file);
      if (processedFile) {
        setUploadFile(processedFile);
        setUploadPreview(URL.createObjectURL(processedFile));
        setShowUploadModal(true);
      }
    } catch (error) {
      console.error('File processing error:', error);
      setFileSizeError('Failed to process image file.');
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/') || isHeicFile(f));
    if (!files.length) return;

    if (isOwnerOrCommittee() && contest?.contestIsGallery && files.length > 1) {
      initBulkQueue(files);
      return;
    }

    const file = files[0];
    try {
      const processedFile = await processImageFile(file);
      if (processedFile) {
        setUploadFile(processedFile);
        setUploadPreview(URL.createObjectURL(processedFile));
        setShowUploadModal(true);
      }
    } catch (error) {
      console.error('File processing error:', error);
      setFileSizeError('Failed to process image file.');
    }
  };

  const renderUploadPlaceholder = () => {
    const isBulkEligible = isOwnerOrCommittee() && contest?.contestIsGallery;
    return (
      <Card
        sx={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: 'primary.main',
          '&:hover': { borderColor: 'primary.dark' }
        }}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Box sx={{ textAlign: 'center' }}>
          <PhotoCameraIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" color="primary">
            {isBulkEligible ? 'Add Photos' : 'Add Photo'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isBulkEligible ? 'Click or drag to upload one or more photos' : 'Click or drag to upload'}
          </Typography>
        </Box>
      </Card>
    );
  };

  const renderPhotoCard = (photo, showActions = false, showVoting = false, showRanking = false, rank = null, showUserName = false, myVoting = false, isAll = true) => (
    <Card key={photo.photoGuid} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {showRanking && rank <= 3 && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          p: 0.5,
          opacity: 0.9,
        }}>
          <TrophyIcon sx={{ color: rank === 1 ? 'gold' : rank === 2 ? 'silver' : '#CD7F32' }} />
        </Box>
      )}

      {showUserName && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          left: showRanking && rank <= 3 ? 40 : 0,
          zIndex: 1,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          p: 0.5,
          fontSize: '0.75rem',
          textAlign: 'center'
        }}>
          {photo.userFullName ?? ''}
        </Box>
      )}

      <CardMedia
        component="img"
        height="200"
        image={photo.thumbUrl}
        alt={photo.photoTitle}
        sx={{
          objectFit: 'cover',
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
          width: 200,
          height: 200,
        }}
        onClick={() => handlePhotoClick(photo)}
      />

      <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
        {photo.photoTitle && (
          <Typography variant="h6" component="h3" gutterBottom>
            {photo.photoTitle}
          </Typography>
        )}
        {photo.photoLocation && (
          <Typography variant="body2" color="text.secondary">
            📍 {photo.photoLocation}
          </Typography>
        )}
        {photo.photoPhotographer && (
          <Typography variant="body2" color="text.secondary">
            📷 {photo.photoPhotographer}
          </Typography>
        )}

        {((isOwnerOrCommittee() && showUserName) || showRanking) && !myVoting && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            Votes: {photo.voteCount || 0}
          </Typography>
        )}

        {(showActions || (isOwnerOrCommittee() && isAll)) && (
          <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex' }}>
            <IconButton
              onClick={() => handleEditPhoto(photo)}
              color="primary"
              sx={{ mr: 0.5 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDeletePhoto(photo.photoGuid)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}

        {showVoting && (
          <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
            {savedGuids.has(photo.photoGuid) && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  right: '50%',
                  transform: 'translateX(50%)',
                  pointerEvents: 'none',
                  fontWeight: 'bold',
                  color: 'primary.main',
                  whiteSpace: 'nowrap',
                  '@keyframes floatUp': {
                    '0%':   { opacity: 1, transform: 'translateX(50%) translateY(0)' },
                    '100%': { opacity: 0, transform: 'translateX(50%) translateY(-24px)' },
                  },
                  animation: 'floatUp 1s ease-out forwards',
                }}
              >
                Saved!
              </Typography>
            )}
            <IconButton
              onClick={() => handleVoteToggle(photo.photoGuid)}
              color={hasVotedForPhoto(photo.photoGuid) ? "primary" : "default"}
              disabled={!hasVotedForPhoto(photo.photoGuid) && votes.length >= contest.contestMaxVotes}
            >
              {hasVotedForPhoto(photo.photoGuid) ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading contest...</Typography>
      </Container>
    );
  }

  if (error || !contest) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error || 'Contest not found'}</Alert>
      </Container>
    );
  }

  const isGallery = !!contest.contestIsGallery;
  const { status, color } = isGallery ? getGalleryStatus(contest) : getContestStatus(contest);
  const remainingUploads = getRemainingUploads();
  const remainingVotes = getRemainingVotes();
  const galleryTotalLimit = isGallery && club ? club.maxMembers * contest.contestMaxUploads : null;
  const galleryAtCapacity = isGallery && galleryTotalLimit !== null && allPhotos.length >= galleryTotalLimit;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom>
            {contest.contestName}
          </Typography>
          <Chip label={status} color={color} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {contest.contestDescription}
          </Typography>
        </Box>

        {isGallery ? (
          <>
          <Alert severity={status === 'Active' ? 'success' : 'info'} sx={{ mb: allPhotos.length > 0 ? 2 : 4 }}>
            {status === 'Active' ? (
              <>
                <Typography variant="h6">Gallery is open!</Typography>
                <Typography>
                  Share your photos • {allPhotos.length} of {galleryTotalLimit ?? '…'} photos • Maximum 20MB per photo
                </Typography>
                {galleryAtCapacity && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Gallery is full - club owner and gallery moderators can clear out old photos
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6">Gallery is closed</Typography>
                <Typography>No new photos can be added.</Typography>
              </>
            )}
          </Alert>
          {allPhotos.length > 0 && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate(`/club/${clubShortName}/contest/${contestShortName}/snappy`)}
                sx={{ px: 4, py: 1.5 }}
              >
                Snappy Showcase
              </Button>
            </Box>
          )}
          </>
        ) : (
          <>
        {status === 'Upcoming' && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="h6">Contest starts soon!</Typography>
            <Typography>
              Photo uploads will begin on {formatDateTime(contest.contestUploadsStart)}
            </Typography>
          </Alert>
        )}

        {status === 'Uploads Open' && (
          <Alert severity="success" sx={{ mb: 4 }}>
            <Typography variant="h6">Upload your photos now!</Typography>
            <Typography>
              Uploads end on {formatDateTime(contest.contestUploadsEnd)} • Maximum {contest.contestMaxUploads} photos per member • You have {remainingUploads} uploads remaining • maximum 20MB per photo
            </Typography>
          </Alert>
        )}

        {status === 'Awaiting Voting' && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            <Typography variant="h6">Upload period has ended</Typography>
            <Typography>
              Voting will begin on {formatDateTime(contest.contestVotesStart)}
            </Typography>
          </Alert>
        )}

        {status === 'Voting Open' && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="h6">Vote for your favorites!</Typography>
            <Typography>
              Voting ends on {formatDateTime(contest.contestVotesEnd)} • You have {contest.contestMaxVotes} votes • {remainingVotes} votes remaining
            </Typography>
          </Alert>
        )}

        {status === 'Awaiting Results' && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            <Typography variant="h6">Voting has ended</Typography>
            <Typography>
              Results will be announced soon!
            </Typography>
          </Alert>
        )}

        {status === 'Contest Results' && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6">🏆 Contest Results! 🏆</Typography>
              <Typography>
                Congratulations to all participants!
              </Typography>
            </Alert>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate(`/club/${clubShortName}/contest/${contestShortName}/snappy`)}
                sx={{ px: 4, py: 1.5 }}
              >
                Snappy Showcase
              </Button>
            </Box>
          </>
        )}
          </>
        )}

        {fileSizeError && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {fileSizeError}
          </Alert>
        )}

        {convertingHeic && (
          <Alert severity="info" sx={{ mb: 4 }}>
            Converting HEIC image to JPEG...
          </Alert>
        )}

        {isGallery ? (
          <>
            {/* Gallery: upload placeholders when active and slots remain */}
            {status === 'Active' && remainingUploads > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  My Gallery Photos ({myPhotos.length})
                </Typography>
                <Grid container spacing={3}>
                  {myPhotos.map(photo => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                      {renderPhotoCard(photo, true, false, false, null, false, false, false)}
                    </Grid>
                  ))}
                  {Array.from({ length: remainingUploads }, (_, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`placeholder-${i}`}>
                      {renderUploadPlaceholder()}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {status === 'Active' && remainingUploads === 0 && myPhotos.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  My Gallery Photos ({myPhotos.length})
                </Typography>
                <Grid container spacing={3}>
                  {myPhotos.map(photo => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                      {renderPhotoCard(photo, true, false, false, null, false, false, false)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* All photos */}
            {allPhotos.length > 0 && (
              <>
                {myPhotos.length > 0 && <Divider sx={{ my: 4 }} />}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Gallery ({allPhotos.length})
                  </Typography>
                  <Grid container spacing={3}>
                    {allPhotos.map(photo => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                        {renderPhotoCard(photo, false, false, false, null, true, false, true)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}

            {allPhotos.length === 0 && myPhotos.length === 0 && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No photos yet. Be the first to share!
              </Typography>
            )}
          </>
        ) : (status === 'Uploads Open' || status === 'Awaiting Voting' || status === 'Voting Open' || status === 'Awaiting Results' || status === 'Contest Results') && (
          <>
            {myPhotos.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  My Photos ({myPhotos.length})
                </Typography>
                <Grid container spacing={3}>
                  {status === 'Contest Results' ? (
                    myPhotos.map((photo, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                        {renderPhotoCard(photo, false, false, false, 0, false, false, false)}
                      </Grid>
                    ))
                  ) : (
                    myPhotos.map(photo => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                        {renderPhotoCard(
                          photo,
                          status === 'Uploads Open',
                          false,
                          false,
                          null,
                          false,
                          false,
                          false
                        )}
                      </Grid>
                    ))
                  )}

                  {status === 'Uploads Open' && Array.from({ length: remainingUploads }, (_, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`placeholder-${i}`}>
                      {renderUploadPlaceholder()}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {status === 'Uploads Open' && myPhotos.length === 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Upload Your Photos
                </Typography>
                <Grid container spacing={3}>
                  {Array.from({ length: remainingUploads }, (_, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`placeholder-${i}`}>
                      {renderUploadPlaceholder()}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {['Uploads Open'].includes(status) && isOwnerOrCommittee() && (
              <>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Members without photos
                  </Typography>
                  <Grid container spacing={3}>
                    {
                      club.clubMembers.filter(member =>
                        !allPhotos.some(photo => photo.userGuid === member.userGuid)
                      ).map(cm => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cm.userGuid}>
                          {cm.userFullName}
                        </Grid>
                      ))
                    }
                  </Grid>
                </Box>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Members with photos
                  </Typography>
                  <Grid container spacing={3}>
                    {
                      club.clubMembers.filter(member =>
                        allPhotos.some(photo => photo.userGuid === member.userGuid)
                      ).map(cm => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cm.userGuid}>
                          {cm.userFullName}
                        </Grid>
                      ))
                    }
                  </Grid>
                </Box>
              </>
            )}
            {['Awaiting Results','Voting Open','Contest Results'].includes(status) && isOwnerOrCommittee() && (
              <>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Non-Voting Members
                  </Typography>
                  <Grid container spacing={3}>
                    {
                      club.clubMembers.filter(member =>
                        !allVotes.some(vote => vote.userVoteGuid === member.userGuid)
                      ).map(cm => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cm.userGuid}>
                          {cm.userFullName}
                        </Grid>
                      ))
                    }
                  </Grid>
                </Box>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Voting Members
                  </Typography>
                  <Grid container spacing={3}>
                    {
                      club.clubMembers.filter(member =>
                        allVotes.some(vote => vote.userVoteGuid === member.userGuid)
                      ).map(cm => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cm.userGuid}>
                          {cm.userFullName}
                        </Grid>
                      ))
                    }
                  </Grid>
                </Box>
              </>
            )}

            {allPhotos.length > 0 && (
              <>
                {myPhotos.length > 0 && (
                  <Divider sx={{ my: 4 }} />
                )}

                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h5" gutterBottom>
                      All Photos ({allPhotos.length})
                    </Typography>
                    {isOwnerOrCommittee() && (
                      <Button variant="outlined" size="small" onClick={() => setSortByVote(!sortByVote)} sx={{marginTop:-1, color:"green"}}>
                        {sortByVote ? 'Sort by Name' : 'Sort by Vote'}
                      </Button>
                    )}
                  </Box>
                  <Grid container spacing={3}>
                    {status === 'Contest Results' ? (
                      getPhotoRanking(allPhotos).map((photo, index, photos) => {
                        // Find unique vote counts higher than current photo's vote count
                        const higherVoteCounts = new Set();
                        for (let i = 0; i < photos.length; i++) {
                          if (photos[i].voteCount > photo.voteCount) {
                            higherVoteCounts.add(photos[i].voteCount);
                          }
                        }

                        const rank = higherVoteCounts.size + 1;

                        // Only show rank badges for ranks 1-3
                        const showRank = rank <= 3;
                        const displayRank = showRank ? rank : null;

                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                            {renderPhotoCard(photo, false, false, showRank, displayRank, true)}
                          </Grid>
                        );
                      })
                    ) : (
                      (sortByVote ? getPhotoRanking([...allPhotos]) : allPhotos).map(photo => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={photo.photoGuid}>
                          {renderPhotoCard(
                            photo,
                            isOwnerOrCommittee() && status === 'Uploads Open',
                            status === 'Voting Open',
                            false,
                            null,
                            true
                          )}
                        </Grid>
                      ))
                    )}
                  </Grid>
                </Box>
              </>
            )}
          </>
        )}

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/heic,image/webp,image/tiff,image/tif,image/bmp"
          multiple={isOwnerOrCommittee() && contest?.contestIsGallery}
          onChange={handleFileSelect}
        />

        <Dialog
          open={showUploadModal}
          onClose={resetUploadModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Upload Photo</DialogTitle>
          <DialogContent>
            {uploadPreview && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <img
                  src={uploadPreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                />
                <Box sx={{ mt: 1 }}>
                  <IconButton onClick={handleRotate} title="Rotate 90° clockwise" size="small">
                    <RotateRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            {isGallery && galleryAtCapacity && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                The gallery is full — uploading this photo will remove the oldest photo.
              </Alert>
            )}

            <TextField
              fullWidth
              label="Photo Title"
              value={photoTitle}
              onChange={(e) => { setPhotoTitle(e.target.value); setPhotoTitleError(false); }}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
              error={photoTitleError}
              helperText={photoTitleError ? 'Photo title is required' : ''}
              ref={photoTitleRef}
            />

            <TextField
              fullWidth
              label="Location"
              value={photoLocation}
              onChange={(e) => { setPhotoLocation(e.target.value); setPhotoLocationError(false); }}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
              error={photoLocationError}
              helperText={photoLocationError ? 'Location is required' : ''}
              ref={photoLocationRef}
            />

            <TextField
              fullWidth
              label="Photographer"
              value={photoPhotographer}
              onChange={(e) => { setPhotoPhotographer(e.target.value); setPhotoPhotographerError(false); }}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
              error={photoPhotographerError}
              helperText={photoPhotographerError ? 'Photographer is required' : ''}
              ref={photoPhotographerRef}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptPolicy}
                  onChange={(e) => setAcceptPolicy(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Link href="/acceptableuse" target="_blank" rel="noopener noreferrer">
                    Acceptable Use Policy
                  </Link>
                </Typography>
              }
              sx={{ mt: 2 }}
              slotProps={{ typography: { component: 'span' } }}
            />

            {showUploadModal && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Leh4YsrAAAAADnPrhjhWQ9E1wL1vAloifB3QOtr"
                  size="invisible"
                  onChange={(value) => {
                    setRecaptchaValue(value);
                    if (value) {
                      handlePhotoUpload(value);
                    }
                  }}
                />
              </Box>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

          </DialogContent>
          <DialogActions>
            <Button onClick={resetUploadModal}>Cancel</Button>
            <Button
              onClick={handleUploadClick}
              variant="contained"
              disabled={uploading || !acceptPolicy}
            >
              {uploading ? <CircularProgress size={20} /> : 'Upload Photo'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk upload dialog — galleries, owner/committee only */}
        <Dialog
          open={showBulkModal}
          onClose={bulkUploading ? undefined : closeBulkModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Upload Photos
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {(() => {
                const done = bulkQueue.filter(i => i.status === 'done').length;
                const total = bulkQueue.length;
                const processing = bulkQueue.some(i => i.status === 'processing');
                if (processing) return `Processing ${total} photo${total !== 1 ? 's' : ''}…`;
                if (bulkUploading) return `Uploading… ${done} of ${total} done`;
                return `${total} photo${total !== 1 ? 's' : ''} selected`;
              })()}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {bulkUploading && (
              <LinearProgress
                variant="determinate"
                value={bulkQueue.length
                  ? bulkQueue.filter(i => i.status === 'done' || i.status === 'rejected').length / bulkQueue.length * 100
                  : 0}
                sx={{ mb: 2 }}
              />
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {bulkQueue.map(item => (
                <Box key={item.id} sx={{ width: 150 }}>
                  <Card>
                    <Box sx={{ position: 'relative', height: 150, bgcolor: 'grey.100' }}>
                      {item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}

                      {item.status === 'uploading' && (
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress size={32} sx={{ color: 'white' }} />
                        </Box>
                      )}

                      {item.status === 'done' && (
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckCircleIcon sx={{ color: 'success.light', fontSize: 40 }} />
                        </Box>
                      )}

                      {item.status === 'rejected' && (
                        <Tooltip title={item.error || 'Upload failed'} arrow>
                          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(180,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help' }}>
                            <ErrorOutlineIcon sx={{ color: 'white', fontSize: 40 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.status === 'pending' && (
                        <IconButton size="small" onClick={() => handleBulkRotate(item.id)} title="Rotate 90° clockwise">
                          <RotateRightIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Card>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
            <Typography variant="body2" color="error.main">
              {bulkQueue.filter(i => i.status === 'rejected').length > 0
                ? `${bulkQueue.filter(i => i.status === 'rejected').length} rejected`
                : ''}
            </Typography>
            <Box>
              <Button onClick={closeBulkModal} disabled={bulkUploading}>
                {bulkQueue.some(i => i.status === 'done') ? 'Close & Refresh' : 'Cancel'}
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkUpload}
                disabled={bulkUploading || !bulkQueue.some(i => i.status === 'pending')}
                sx={{ ml: 1 }}
              >
                {bulkUploading
                  ? <CircularProgress size={20} />
                  : `Upload ${bulkQueue.filter(i => i.status === 'pending').length} Photo${bulkQueue.filter(i => i.status === 'pending').length !== 1 ? 's' : ''}`}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showEditModal}
          onClose={resetEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Photo</DialogTitle>
          <DialogContent>
            {editingPhoto && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <img
                  src={editingPhoto.thumbUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                />
              </Box>
            )}

            {editError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Photo Title"
              value={editPhotoTitle}
              onChange={(e) => setEditPhotoTitle(e.target.value)}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              label="Location"
              value={editPhotoLocation}
              onChange={(e) => setEditPhotoLocation(e.target.value)}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              label="Photographer"
              value={editPhotoPhotographer}
              onChange={(e) => setEditPhotoPhotographer(e.target.value)}
              margin="normal"
              required={!isGallery}
              inputProps={{ maxLength: 100 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={resetEditModal}>Cancel</Button>
            <Button
              onClick={handlePhotoEdit}
              variant="contained"
              disabled={editing || (!isGallery && (!editPhotoTitle || !editPhotoLocation || !editPhotoPhotographer))}
            >
              {editing ? <CircularProgress size={20} /> : 'Update Photo'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showPhotoViewer}
          onClose={handleClosePhotoViewer}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              boxShadow: 'none',
            }
          }}
        >
          {selectedPhoto && (
            <>
              <DialogContent
                sx={{
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <Box sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 1,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: 1,
                  p: 1
                }}>
                  <IconButton
                    onClick={handleZoomOut}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ color: 'white', minWidth: '50px', textAlign: 'center' }}>
                    {Math.round((zoomLevel / baseScale) * 100)}%
                  </Typography>
                  <IconButton
                    onClick={handleZoomIn}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleZoomReset}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <ZoomResetIcon />
                  </IconButton>
                </Box>

                <Box
                  ref={containerRef}
                  sx={{
                    width: '100%',
                    height: '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    cursor: zoomLevel > baseScale ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onWheel={handleWheel}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <img
                    ref={imageRef}
                    src={selectedPhoto.photoUrl}
                    alt={selectedPhoto.photoTitle}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      transform: `scale(${zoomLevel / baseScale}) translate(${panPosition.x / (zoomLevel / baseScale)}px, ${panPosition.y / (zoomLevel / baseScale)}px)`,
                      transition: isDragging ? 'none' : 'transform 0.2s ease',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                    onLoad={measureImageScale}
                    onDoubleClick={handleImageDoubleClick}
                    draggable={false}
                  />
                </Box>

                <Box sx={{ p: 2, textAlign: 'center', color: 'white' }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedPhoto.photoTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    📍 {selectedPhoto.photoLocation}
                  </Typography>
                  <Typography variant="body2">
                    📷 {selectedPhoto.photoPhotographer}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button onClick={handleClosePhotoViewer} sx={{ color: 'white' }}>
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ContestPage;